import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FaDownload } from "react-icons/fa6"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { Field } from "../chakra/Field"
import { GroupedFieldSelector } from "./GroupedFieldSelector"
import { BandedTable } from "./BandedTable"
import { exportCsv, type ReportingMeta } from "./downloadCsv"
import type { ReportingScope } from "./reportingClient"
import { FIELD_GROUPS } from "./fieldGroups"
import { pruneHydrogenWeight } from "./pruneHydrogenWeight"
import { FREQUENCY_OPTIONS } from "./frequency"
import {
  useDownloadPreferences,
  formatDateLabel,
  STORAGE_KEY_FREQUENCY,
  STORAGE_KEY_START,
  STORAGE_KEY_STOP,
  getInitialFrequency,
  getInitialDateRange,
  persistDownloadPreferences,
} from "./useDownloadPreferences"
import { useReportingData, formatReportingError } from "./useReportingData"

export {
  STORAGE_KEY_FREQUENCY,
  STORAGE_KEY_START,
  STORAGE_KEY_STOP,
  getInitialFrequency,
  getInitialDateRange,
  persistDownloadPreferences,
}

export interface HybridDownloadProps {
  /** The reporting scope, mapped onto the REST path segment. */
  scope: ReportingScope
  /** The entity ID to fetch reporting data for. */
  id: string
  /** Human-readable display name, used for the CSV filename. */
  name: string
  /** Earliest allowed date (e.g. from installation/assignment start), YYYY-MM-DD */
  minDate?: string
  /** Latest allowed date (e.g. from installation/assignment end), YYYY-MM-DD */
  maxDate?: string
  /** When true, hydrogen weight columns are excluded from the download (refuelling makes them misleading) */
  hasElectrolyser?: boolean
}

/** Max rows to show in the table preview */
const PREVIEW_ROW_LIMIT = 10

/**
 * HybridDownload is the scope-parameterised orchestrator component for the
 * Hybrid Timescale Reporting download feature. It fetches directly from the
 * Timeseries REST API (via {@link useReportingData}) for any of the
 * `systems`, `assets`, or `customers` scopes and renders the shared field
 * selector, banded preview table, and CSV export.
 *
 * State for custom dates and granularities is managed and persisted by
 * {@link useDownloadPreferences}. Data responses are cached by React Query
 * to eliminate duplicate fetches across tab navigation and back/forward actions.
 */
export function HybridDownload({
  scope,
  id,
  name,
  minDate,
  maxDate,
  hasElectrolyser = false,
}: HybridDownloadProps) {
  const {
    start,
    stop,
    frequency,
    draftStart,
    draftStop,
    draftFrequency,
    today,
    hasChanges,
    isValidRange,
    handleStartChange,
    handleStopChange,
    handleFrequencyChange,
    handleApply,
    handleApplyPreset,
    handleKeyDown,
  } = useDownloadPreferences({ minDate, maxDate })

  // Field selection state
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [csvError, setCsvError] = useState<string | null>(null)

  /** All non-period field keys (always fetch all for complete available_fields meta) */
  const allFieldKeys = useMemo(() => {
    return FIELD_GROUPS.flatMap((g) => g.fields)
      .filter((f) => f.key !== "bucket_start" && f.key !== "bucket_end")
      .map((f) => f.key)
  }, [])

  // React Query cached fetch: keeps data fresh for 5 mins, in memory for 10 mins.
  // Tab-switching and back/forward navigation retrieve cached data instantly without refetching.
  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useReportingData({
    scope,
    id,
    start,
    stop,
    frequency,
    fields: allFieldKeys,
  })

  const error = csvError || formatReportingError(queryError)

  /** Available fields from the API response meta, pruning hydrogen weight fields when an electrolyser is present */
  const availableFields = useMemo(() => {
    const fields = data?.meta?.available_fields ?? []
    return pruneHydrogenWeight(fields, hasElectrolyser)
  }, [data, hasElectrolyser])

  /** Initialise selected fields when available fields first load */
  useEffect(() => {
    if (availableFields.length > 0 && selectedFields.length === 0) {
      setSelectedFields(["bucket_start", "bucket_end", ...availableFields])
    }
  }, [availableFields, selectedFields.length])

  /** Handle CSV download */
  const handleDownloadCsv = useCallback(() => {
    if (!data || data.rows.length === 0) return

    try {
      const meta: ReportingMeta = {
        system_id: id,
        system_name: name,
        start,
        stop,
        frequency,
        timezone: data.meta.timezone ?? IANATimeZone(),
      }

      const csvFields = selectedFields.filter(
        (f) => f !== "bucket_start" && f !== "bucket_end",
      )

      exportCsv(data.rows, csvFields, meta)
    } catch {
      setCsvError("Failed to generate CSV. Please try again.")
    }
  }, [data, selectedFields, id, name, start, stop, frequency])

  const rows = data?.rows ?? []
  const previewRows = rows.slice(0, PREVIEW_ROW_LIMIT)
  const hasData = rows.length > 0
  const downloadDisabled = loading || !hasData

  return (
    <VStack align="stretch" gap={4}>
      <VStack align="stretch" gap={4}>
        <HStack justifyContent="space-between" flexWrap="wrap" gap={2} alignItems="center">
          <Heading as="h2" size="md">
            Select data frequency and time period
          </Heading>
          {minDate && maxDate && (
            <HStack gap={2}>
              <Text fontSize="xs" color="gray.600">
                Available:
              </Text>
              <Button
                variant="outline"
                size="xs"
                onClick={() => handleApplyPreset(minDate, maxDate)}
              >
                {formatDateLabel(minDate)} – {formatDateLabel(maxDate)}
              </Button>
            </HStack>
          )}
        </HStack>

        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={4}
          align="flex-start"
          wrap="wrap"
        >
          {/* Frequency selector */}
          <Box flex={{ base: "1 1 100%", sm: "0 0 160px" }}>
            <Field label="Frequency" helperText="Bucket resolution">
              <NativeSelectRoot>
                <NativeSelectField
                  value={draftFrequency}
                  onChange={handleFrequencyChange}
                  aria-label="Frequency"
                >
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            </Field>
          </Box>

          {/* Start date */}
          <Box flex={{ base: "1 1 100%", sm: "0 0 180px" }}>
            <Field
              label="Start"
              helperText={formatDateLabel(draftStart)}
              invalid={Boolean(draftStart && draftStop && draftStart > draftStop)}
              errorText="Cannot be after End date"
            >
              <Input
                type="date"
                value={draftStart}
                max={today}
                onChange={handleStartChange}
                onKeyDown={handleKeyDown}
                aria-label="Start date"
              />
            </Field>
          </Box>

          {/* End date */}
          <Box flex={{ base: "1 1 100%", sm: "0 0 180px" }}>
            <Field
              label="End"
              helperText={formatDateLabel(draftStop)}
              invalid={Boolean(draftStart && draftStop && draftStart > draftStop)}
              errorText="Cannot be before Start date"
            >
              <Input
                type="date"
                value={draftStop}
                max={today}
                onChange={handleStopChange}
                onKeyDown={handleKeyDown}
                aria-label="End date"
              />
            </Field>
          </Box>

          {/* Apply button */}
          <Box flex={{ base: "1 1 100%", sm: "0 0 auto" }}>
            <Field
              label={<Text as="span" visibility="hidden">&nbsp;</Text>}
              helperText={<Text as="span" visibility="hidden">&nbsp;</Text>}
            >
              <Button
                variant="hydrologiq_primary"
                size="md"
                onClick={handleApply}
                disabled={!hasChanges || !isValidRange || loading}
                loading={loading}
                minW="100px"
                width={{ base: "100%", sm: "auto" }}
              >
                Apply
              </Button>
            </Field>
          </Box>
        </Flex>

        {/* Results heading + actions (field selector and download on same side) */}
        <HStack justifyContent="space-between" flexWrap="wrap" gap={3}>
          <Heading as="h3" size="md">
            Results (first {PREVIEW_ROW_LIMIT} items)
          </Heading>
          <HStack gap={2}>
            <GroupedFieldSelector
              groups={FIELD_GROUPS}
              availableFields={availableFields}
              selectedFields={selectedFields}
              onFieldsChange={setSelectedFields}
            />
            <Button
              variant="hydrologiq_primary"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={downloadDisabled}
              loading={loading}
            >
              <HStack gap={2}>
                <FaDownload />
                <Text>Download CSV</Text>
              </HStack>
            </Button>
          </HStack>
        </HStack>
      </VStack>

      {/* Error message */}
      {error && !loading && (
        <Box p={4} borderRadius="md" bg="red.50" borderWidth="1px" borderColor="red.200">
          <Text color="red.700">{error}</Text>
        </Box>
      )}

      {/* Data table preview (limited to PREVIEW_ROW_LIMIT rows) */}
      <BandedTable
        rows={previewRows}
        selectedFields={selectedFields}
        groups={FIELD_GROUPS}
        loading={loading}
      />

      {/* Caption below table showing timezone info */}
      {hasData && !loading && (
        <Text fontSize="sm" color="gray.500">
          Reporting preview (date timezone is {IANATimeZone()})
        </Text>
      )}
    </VStack>
  )
}

export default HybridDownload

